import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { CM2Dataset, ControlsHubChartData } from '@interfaces/central-monitoring';
import { statusRAGChips } from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/helper';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import { AuditApplicationArea, AuditDataType, AuditServiceType, PanelType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useLocalState } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate, stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { cm2Panel_OpraQueries } from '@queries/generated/central-monitoring.generated';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';

type ControlsHubChartGridProps = {
    studyId?: string;
    dataset: CM2Dataset;
    chartId?: string;
    panelData?: cm2Panel_OpraQueries;
};

const ControlsHubChartGrid = ({ studyId, dataset, chartId, panelData }: ControlsHubChartGridProps) => {
    const user = useAuthorization();
    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;
    if (
        !user?.can?.({
            module: Constants.Modules.RAM,
            page: Constants.PageElements.CMControls,
            permission: Permissions.Enabled,
            element: Constants.PageElements.CMControls,
        })
    )
        return;

    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    const { ChipCell } = useGridCustomCell();
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.popOut.chartGrid' });
    const { t: tRiskLog } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const chips = useMemo(() => statusRAGChips(tRiskLog), []);
    // eslint-disable-next-line max-len
    const exportFileName = `${studyName}_Dataset ${stringDateWithoutTimeFromUtc(dataset.dataCutDate, 'DD-MMM-YYYY')}_${panelData?.cm2Panel?.plotTitle}`;
    const [chartData, setChartData] = useState<ControlsHubChartData[] | undefined>(undefined);
    const { data } = ControlsHubApi.useGetControlsHubChartData(studyId, dataset.id, chartId);
    const yValueTitle = useRef<string>('');
    const numeratorEventsTitle = useRef<string>('');
    const denominatorValueTitle = useRef<string>('');

    if (panelData && panelData.cm2Panel) {
        if (panelData.cm2Panel.panelType === PanelType.EVENT_RATES_OVER_TIME) {
            yValueTitle.current = panelData.cm2Panel.eventRatesOverTimeParameters?.yAxisTitle ?? '';
            numeratorEventsTitle.current = panelData.cm2Panel.eventRatesOverTimeParameters?.numeratorName ?? '';
            denominatorValueTitle.current = panelData.cm2Panel.eventRatesOverTimeParameters?.denominatorName ?? '';
        } else {
            yValueTitle.current = panelData.cm2Panel.proportionsOverTimeParameters?.yAxisTitle ?? '';
            numeratorEventsTitle.current = panelData.cm2Panel.proportionsOverTimeParameters?.numeratorName ?? '';
            denominatorValueTitle.current = panelData.cm2Panel.proportionsOverTimeParameters?.denominatorName ?? '';
        }
    }

    useEffect(() => {
        if (data && panelData) {
            setChartData(data);
        }
    }, [data, panelData]);

    const columns: GridColumnProps[] = [
        {
            id: 'binLabelDate',
            field: 'binLabelDate',
            title: t('columns.timePeriod'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            width: 1,
            minWidth: 150,
            id: 'yValue',
            field: 'yValue',
            title: yValueTitle.current,
            filter: 'numeric',
            format: '{0:0.00}',
        },
        {
            width: 1,
            minWidth: 150,
            id: 'numeratorEvents',
            field: 'numeratorEvents',
            title: numeratorEventsTitle.current,
            filter: 'numeric',
        },
        {
            width: 1,
            minWidth: 150,
            id: 'denominatorValue',
            field: 'denominatorValue',
            title: denominatorValueTitle.current,
            filter: 'numeric',
            format: '{0:0.00}',
        },
        {
            width: 1,
            minWidth: 150,
            id: 'scaleBy',
            field: 'scaleBy',
            title: t('columns.scaleBy'),
            filter: 'text',
        },
        {
            width: 1,
            minWidth: 150,
            id: 'status',
            field: 'colourDirectionThreshold',
            title: t('columns.status'),
            lovData: chips.map((c) => c.value),
            cells: { data: ChipCell(chips, { width: 95 }) },
        },
        {
            width: 1,
            minWidth: 150,
            id: 'outOfToleranceValue',
            field: 'outOfToleranceValue',
            title: t('columns.ootValue'),
            filter: 'numeric',
            format: '{0:0.00}',
        },
        {
            id: 'toleranceLimits',
            field: 'toleranceLimits',
            sortable: false,
            title: t('columns.toleranceLimits'),
            children: [
                {
                    width: 1,
                    minWidth: 100,
                    id: 'benchmarkValue',
                    field: 'benchmarkValue',
                    title: t('columns.benchmark'),
                    filter: 'numeric',
                    format: '{0:0.00}',
                },
                {
                    width: 1,
                    minWidth: 100,
                    id: 'upperRedValue',
                    field: 'upperRedValue',
                    title: t('columns.upperRed'),
                    filter: 'numeric',
                    format: '{0:0.00}',
                },
                {
                    width: 1,
                    minWidth: 100,
                    id: 'upperAmberValue',
                    field: 'upperAmberValue',
                    title: t('columns.upperAmber'),
                    filter: 'numeric',
                    format: '{0:0.00}',
                },
                {
                    width: 1,
                    minWidth: 100,
                    id: 'lowerAmberValue',
                    field: 'lowerAmberValue',
                    title: t('columns.lowerAmber'),
                    filter: 'numeric',
                    format: '{0:0.00}',
                },
                {
                    width: 1,
                    minWidth: 100,
                    id: 'lowerRedValue',
                    field: 'lowerRedValue',
                    title: t('columns.lowerRed'),
                    filter: 'numeric',
                    format: '{0:0.00}',
                },
            ],
        },
        {
            width: 1,
            minWidth: 150,
            id: 'plotCalculation',
            field: 'plotCalculation',
            title: t('columns.plotCalculations'),
            filter: 'text',
        },
        {
            width: 1,
            minWidth: 150,
            id: 'uniqueSubjectsInBin',
            field: 'uniqueSubjectsInBin',
            title: t('columns.numSubjects'),
            filter: 'numeric',
        },
        {
            width: 1,
            minWidth: 150,
            id: 'expectedVolume',
            field: 'expectedVolume',
            title: t('columns.studyExpectedVolume'),
            filter: 'numeric',
        },
    ];

    return (
        <div style={{ height: '100%' }} className="remove-card-padding">
            <Grid
                excludeHiddenRowMenuActions
                title={panelData?.cm2Panel?.plotTitle ?? ''}
                maxHeight="100%"
                testDataId="risklog-cm-controls-popout-chart-grid"
                data={chartData}
                columns={columns}
                filterable
                showAddIcon={false}
                useLoadingOverlay
                canEdit={canEdit}
                loading={!chartData}
                scrollable="virtual"
                dataItemKey="id"
                exportFilename={`${exportFileName}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY')}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                removeCardBorder
                persistentStorageKey={String(PersistentStorageKeys['cm-controls-popout-top-grid'])}
            />
        </div>
    );
};
export default ControlsHubChartGrid;
